import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faClock,
  faVideo,
  faGlobe,
  faArrowRight,
  faDownload,
  faLaptop,
  faVolumeHigh,
  faDoorOpen,
  faChalkboardTeacher,
  faCheck,
  faStar,
  faXmark,
  faHourglassHalf,
  faCircleExclamation,
  faCircleCheck,
  faTriangleExclamation,
  faCalendarPlus,
  faGraduationCap,
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import './DemoClass.css';

/* Only save a real place time zone ("Asia/Beirut"). A device set to a raw offset gives
   "Etc/GMT+8", "UTC" or "GMT" — no city, no flag, often wrong — and used to overwrite the
   time zone chosen in the admin dashboard on every sign-in. */
const isRealZone = (tz) => typeof tz === 'string' && tz.includes('/') && !tz.startsWith('Etc/');

const API = process.env.REACT_APP_API_BASE_URL;
const JOIN_EARLY_MIN = 5; // students can join 5 min before (same as the server message)
const CLASS_LENGTH_MIN = 60; // treat the class as "live" up to 60 min after start

const WHY_CONTINUE = [
  'Same teacher, 1-to-1, every class',
  'A real project in every session',
  'Times that fit your schedule',
  'Progress reports and a certificate',
];

const shortName = (full = '') => {
  const [first, last] = full.trim().split(/\s+/);
  return last ? `${first} ${last[0].toUpperCase()}.` : first || 'Student';
};

const initialsOf = (full = '') =>
  full
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase())
    .join('') || 'S';

const formatCountdown = (ms) => {
  const totalMin = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(totalMin / 1440);
  const h = Math.floor((totalMin % 1440) / 60);
  const m = totalMin % 60;
  if (d > 0) return `${d} day${d > 1 ? 's' : ''} ${h}h`;
  if (h > 0) return `${h}h ${m}min`;
  return `${m} min`;
};

const DemoClass = () => {
  const navigate = useNavigate();
  const studentId = sessionStorage.getItem('demoStudentId');
  const PHONE = process.env.REACT_APP_MOBILE_NUMBER1;

  const [studentData, setStudentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(() => new Date());
  const [joining, setJoining] = useState(false);
  const [dialog, setDialog] = useState(null); // { type, message }
  const [rating, setRating] = useState(0);

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const waUrl = (text) =>
    `https://api.whatsapp.com/send/?phone=${PHONE}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

  // ---------- Student details (same endpoint) ----------
  useEffect(() => {
    if (!studentId) {
      navigate('/joinfreeclass', { replace: true });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await axios.get(`${API}/api/demo/details/${studentId}`);
        if (!cancelled) setStudentData(response.data);
      } catch (err) {
        if (!cancelled) setError('We couldn’t load your class details.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [studentId, navigate]);

  // ---------- Save the student's time zone (same logic as before) ----------
  useEffect(() => {
    if (!studentId || !isRealZone(timeZone)) return;
    const auth = { headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } };
    (async () => {
      try {
        const offsetHours = -new Date().getTimezoneOffset() / 60;
        const res = await axios.get(`${API}/api/student/dashboard/OffsetList`, auth);

        let list = res.data;
        if (!Array.isArray(list) && list && typeof list === 'object') {
          list =
            (Array.isArray(list.data) && list.data) ||
            (Array.isArray(list.recordset) && list.recordset) ||
            (Array.isArray(list.result) && list.result) ||
            Object.values(list).filter((item) => item && typeof item === 'object' && item.Id !== undefined);
        }
        if (!Array.isArray(list)) list = [];

        let settingId = list.find((s) => s.Timezone === timeZone)?.Id ?? null;
        if (!settingId) {
          const added = await axios.post(`${API}/api/student/dashboard/addTimezone`, { timezone: timeZone, offsetHours }, auth);
          settingId = added.data.id;
        }
        if (settingId) {
          await axios.put(`${API}/api/student/dashboard/updateOffset/${studentId}`, { offsetId: settingId }, auth);
        }
      } catch (err) {
        console.error('Error detecting/updating timezone:', err);
      }
    })();
  }, [studentId, timeZone]);

  // ---------- Clock for the countdown (every 30s is enough) ----------
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30000);
    return () => clearInterval(t);
  }, []);

  // ---------- Join (same server checks; dialogs instead of injected HTML) ----------
  const handleJoinClass = async (classId, isLive) => {
    // When the class is live, open the tab right away so phones/Safari
    // don't block Zoom as a popup (it opens after the server checks)
    const zoomTab = isLive ? window.open('', '_blank') : null;
    setJoining(true);
    try {
      const response = await axios.post(`${API}/api/classes/check-availability`, { classId });
      const { status, message, zoomUrl } = response.data;

      if (status === 'joining') {
        const joinResponse = await axios.post(`${API}/api/classes/confirm-join/studentjoin`, { classId });
        if (joinResponse.data.success && zoomUrl) {
          if (zoomTab) zoomTab.location.href = zoomUrl;
          else window.location.href = zoomUrl;
          return;
        }
        zoomTab?.close();
        setDialog({ type: 'error', message: 'We couldn’t open the class. Please try again.' });
        return;
      }

      zoomTab?.close();
      if (status === 'early') setDialog({ type: 'early', message });
      else if (status === 'expired') setDialog({ type: 'expired', message });
      else setDialog({ type: 'error', message: message || 'Please try again.' });
    } catch (err) {
      zoomTab?.close();
      console.error('Join error:', err);
      setDialog({ type: 'error', message: 'Something went wrong. Please try again.' });
    } finally {
      setJoining(false);
    }
  };

  // Escape closes the dialog
  useEffect(() => {
    if (!dialog) return undefined;
    const onKey = (e) => e.key === 'Escape' && setDialog(null);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [dialog]);

  const scrollToPricing = () => {
    document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  // ---------- Loading ----------
  if (loading) {
    return (
      <div className="rmdc" aria-busy="true">
        <div className="rmdc-inner">
          <div className="rmdc-skel rmdc-skel--title" />
          <div className="rmdc-grid">
            <div className="rmdc-skel rmdc-skel--card" />
            <div className="rmdc-skel rmdc-skel--card" />
          </div>
        </div>
      </div>
    );
  }

  // ---------- Error ----------
  if (error) {
    return (
      <div className="rmdc">
        <div className="rmdc-state">
          <FontAwesomeIcon icon={faCircleExclamation} className="rmdc-state-icon" />
          <h1 className="rmdc-state-title">Something went wrong</h1>
          <p>{error} Please try again, or message us and we’ll send your class link.</p>
          <div className="rmdc-state-actions">
            <button type="button" className="rmdc-btn" onClick={() => window.location.reload()}>
              Try again
            </button>
            <a className="rmdc-btn rmdc-btn--wa" href={waUrl('Hi! I can’t open my free class dashboard.')} target="_blank" rel="noopener noreferrer">
              <FontAwesomeIcon icon={faWhatsapp} /> Message us
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ---------- Class status ----------
  const fullName = studentData?.student?.fullName || '';
  const firstName = fullName.split(' ')[0] || 'there';
  const session = studentData?.schedule?.[0] || null;
  const start = session?.date ? new Date(session.date) : null;
  const validStart = start && !Number.isNaN(start.getTime());

  let status = 'none';
  if (validStart) {
    const diff = start - now;
    if (diff > JOIN_EARLY_MIN * 60000) status = 'upcoming';
    else if (diff > -CLASS_LENGTH_MIN * 60000) status = 'live';
    else status = 'ended';
  }

  const dateLabel = validStart
    ? start.toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })
    : '';
  const timeLabel = validStart ? start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
  const teacher = session?.teacher || 'your teacher';
  const course = studentData?.student?.interestedCourse || '';

  // ---------- Add to calendar ----------
  const end = validStart ? new Date(start.getTime() + 50 * 60000) : null;
  const icsDate = (d) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z';
  const calTitle = 'RoboMind free coding class';
  const calDetails = `Live 1-to-1 class on Zoom with ${teacher}. Join from your Trial Dashboard: ${window.location.origin}/joinfreeclass`;
  const googleCalUrl = validStart
    ? `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(calTitle)}&dates=${icsDate(
        start
      )}/${icsDate(end)}&details=${encodeURIComponent(calDetails)}`
    : '';

  const downloadIcs = () => {
    if (!validStart) return;
    const ics = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//RoboMind//Trial Class//EN',
      'BEGIN:VEVENT',
      `UID:robomind-trial-${studentId}@robomind-edu.com`,
      `DTSTAMP:${icsDate(new Date())}`,
      `DTSTART:${icsDate(start)}`,
      `DTEND:${icsDate(end)}`,
      `SUMMARY:${calTitle}`,
      `DESCRIPTION:${calDetails}`,
      'BEGIN:VALARM',
      'TRIGGER:-PT15M',
      'ACTION:DISPLAY',
      'DESCRIPTION:Your free class starts in 15 minutes',
      'END:VALARM',
      'END:VEVENT',
      'END:VCALENDAR',
    ].join('\r\n');
    const url = URL.createObjectURL(new Blob([ics], { type: 'text/calendar' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'robomind-free-class.ics';
    a.click();
    URL.revokeObjectURL(url);
  };

  const enrollText = `Hi! ${firstName} had a free trial class with ${teacher} and we'd like to enroll.`;
  const recent = (studentData?.lastClasses || []).slice(0, 3);

  return (
    <div className="rmdc">
      <div className="rmdc-inner">
        {/* ---------- Greeting ---------- */}
        <header className="rmdc-head">
          <span className="rmdc-eyebrow">Trial Dashboard</span>
          <h1 className="rmdc-title">
            Welcome, <span>{firstName}</span>!
          </h1>
          <p className="rmdc-sub">
            {status === 'ended'
              ? 'Thanks for joining your free class. Here’s how to keep going.'
              : 'Everything you need for your free coding class is here.'}
          </p>
        </header>

        <div className="rmdc-grid">
          {/* ---------- Class card ---------- */}
          <section className={`rmdc-card ${status === 'live' ? 'rmdc-card--live' : ''}`} aria-labelledby="rmdc-card-title">
            {status === 'none' && (
              <>
                <div className="rmdc-card-top">
                  <span className="rmdc-pill">
                    <FontAwesomeIcon icon={faHourglassHalf} /> Not scheduled yet
                  </span>
                </div>
                <h2 id="rmdc-card-title" className="rmdc-card-title">
                  Your class time isn’t set yet
                </h2>
                <p className="rmdc-card-text">Message us and we’ll pick a time that suits you.</p>
                <a className="rmdc-btn rmdc-btn--wa" href={waUrl(`Hi! I'd like to schedule ${firstName}'s free class.`)} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faWhatsapp} /> Pick a time on WhatsApp
                </a>
              </>
            )}

            {status !== 'none' && (
              <>
                <div className="rmdc-card-top">
                  {status === 'live' && (
                    <span className="rmdc-pill is-live">
                      <span className="rmdc-live-dot" aria-hidden="true" /> Live now
                    </span>
                  )}
                  {status === 'upcoming' && (
                    <span className="rmdc-pill">
                      <FontAwesomeIcon icon={faClock} /> Starts in {formatCountdown(start - now)}
                    </span>
                  )}
                  {status === 'ended' && (
                    <span className="rmdc-pill is-done">
                      <FontAwesomeIcon icon={faCircleCheck} /> Class finished
                    </span>
                  )}
                </div>

                <h2 id="rmdc-card-title" className="rmdc-card-title">
                  {status === 'live' ? 'Your class is starting!' : 'Your free class'}
                </h2>
                <p className="rmdc-card-text">
                  Live 1-to-1 on Zoom with <strong>{teacher}</strong>
                </p>

                <ul className="rmdc-facts">
                  <li>
                    <FontAwesomeIcon icon={faCalendarAlt} /> {dateLabel}
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faClock} /> {timeLabel} <small>your time</small>
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faVideo} /> About 50 min
                  </li>
                  <li>
                    <FontAwesomeIcon icon={faGraduationCap} /> {course || 'The teacher will pick the right course'}
                  </li>
                </ul>

                {status !== 'ended' ? (
                  <>
                    <button
                      type="button"
                      className="rmdc-btn rmdc-btn--join"
                      onClick={() => handleJoinClass(session.scheduleid, status === 'live')}
                      disabled={joining}
                    >
                      {joining ? (
                        <span className="rmdc-spinner" aria-label="Opening class" />
                      ) : (
                        <>
                          <FontAwesomeIcon icon={faVideo} />
                          Join class
                          <FontAwesomeIcon icon={faArrowRight} />
                        </>
                      )}
                    </button>

                    {status === 'upcoming' && (
                      <div className="rmdc-cal">
                        <span>Add to calendar:</span>
                        <a href={googleCalUrl} target="_blank" rel="noopener noreferrer">
                          <FontAwesomeIcon icon={faCalendarPlus} /> Google
                        </a>
                        <button type="button" onClick={downloadIcs}>
                          <FontAwesomeIcon icon={faCalendarPlus} /> Apple / Outlook
                        </button>
                      </div>
                    )}

                    <p className="rmdc-note">
                      <FontAwesomeIcon icon={faGlobe} /> Times in your time zone ({timeZone}). You can join{' '}
                      {JOIN_EARLY_MIN} minutes before the start.
                    </p>
                  </>
                ) : (
                  <p className="rmdc-finished" role="status">
                    <FontAwesomeIcon icon={faCircleCheck} /> This class has finished. Thanks for joining, {firstName}!
                  </p>
                )}
              </>
            )}
          </section>

          {/* ---------- Side ---------- */}
          <aside className="rmdc-side" aria-labelledby="rmdc-side-title">
            {status === 'ended' ? (
              <>
                <h2 id="rmdc-side-title" className="rmdc-side-title">
                  Why families continue
                </h2>
                <ul className="rmdc-why">
                  {WHY_CONTINUE.map((t) => (
                    <li key={t}>
                      <FontAwesomeIcon icon={faCheck} /> {t}
                    </li>
                  ))}
                </ul>
              </>
            ) : (
              <>
                <h2 id="rmdc-side-title" className="rmdc-side-title">
                  Get ready for class
                </h2>
                <ul className="rmdc-setup">
                  <li>
                    <span className="rmdc-setup-icon">
                      <FontAwesomeIcon icon={faDownload} />
                    </span>
                    <div>
                      <strong>Install Zoom</strong>
                      <p>The desktop app works best.</p>
                      <a href="https://zoom.us/download" target="_blank" rel="noopener noreferrer">
                        Download Zoom <FontAwesomeIcon icon={faArrowRight} />
                      </a>
                    </div>
                  </li>
                  <li>
                    <span className="rmdc-setup-icon">
                      <FontAwesomeIcon icon={faLaptop} />
                    </span>
                    <div>
                      <strong>Use a computer</strong>
                      <p>A laptop or PC, or an iPad with a mouse.</p>
                    </div>
                  </li>
                  <li>
                    <span className="rmdc-setup-icon">
                      <FontAwesomeIcon icon={faVolumeHigh} />
                    </span>
                    <div>
                      <strong>Quiet place, sound on</strong>
                      <p>Check the microphone and speakers.</p>
                    </div>
                  </li>
                  <li>
                    <span className="rmdc-setup-icon">
                      <FontAwesomeIcon icon={faDoorOpen} />
                    </span>
                    <div>
                      <strong>Join a few minutes early</strong>
                      <p>The button opens 5 minutes before.</p>
                    </div>
                  </li>
                </ul>
              </>
            )}

            {session && (
              <div className="rmdc-teacher">
                <span className="rmdc-teacher-icon">
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </span>
                <div>
                  <strong>{teacher}</strong>
                  <span>Your teacher</span>
                </div>
              </div>
            )}
          </aside>
        </div>

        {/* ---------- After the class: feedback + next step ---------- */}
        {status === 'ended' && (
          <section className="rmdc-after" aria-labelledby="rmdc-after-title">
            <h2 id="rmdc-after-title" className="rmdc-after-title">
              How was the free class?
            </h2>
            <div className="rmdc-rate" role="radiogroup" aria-label="Rate the free class">
              {[1, 2, 3, 4, 5].map((n) => (
                <button
                  key={n}
                  type="button"
                  role="radio"
                  aria-checked={rating === n}
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                  className={`rmdc-rate-star ${n <= rating ? 'is-on' : ''}`}
                  onClick={() => setRating(n)}
                >
                  <FontAwesomeIcon icon={faStar} />
                </button>
              ))}
            </div>

            {rating > 0 && (
              <p className="rmdc-after-thanks">
                {rating >= 4
                  ? `Thank you! We’re happy ${firstName} enjoyed it.`
                  : 'Thanks for telling us. We’d love to hear how we can do better.'}{' '}
                <a
                  href={waUrl(`Hi! We rated ${firstName}'s free class ${rating}/5 ⭐. `)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Send your feedback
                </a>
              </p>
            )}

            <p className="rmdc-card-text">
              {firstName} can continue with {teacher}, one-to-one, at times that suit you. We’ll recommend the right
              course for {firstName}.
            </p>
            <div className="rmdc-cta-row">
                  <a className="rmdc-btn rmdc-btn--wa" href={waUrl(enrollText)} target="_blank" rel="noopener noreferrer">
                    <FontAwesomeIcon icon={faWhatsapp} /> Enroll on WhatsApp
                  </a>
                  <button type="button" className="rmdc-btn rmdc-btn--ghost" onClick={scrollToPricing}>
                    See packages <FontAwesomeIcon icon={faArrowRight} />
                  </button>
                </div>
            <a
              className="rmdc-missed"
              href={waUrl(`Hi! ${firstName} missed the free class. Can we book another time?`)}
              target="_blank"
              rel="noopener noreferrer"
            >
              Missed the class? Book another time <FontAwesomeIcon icon={faArrowRight} />
            </a>
          </section>
        )}

        {/* ---------- Recent classes from other students ---------- */}
        {recent.length > 0 && (
          <section className="rmdc-recent" aria-labelledby="rmdc-recent-title">
            <h2 id="rmdc-recent-title" className="rmdc-recent-title">
              What our students are building
            </h2>
            <div className="rmdc-recent-grid">
              {recent.map((c, i) => (
                <article className="rmdc-recent-card" key={`${c.courseName}-${i}`}>
                  {c.courseImage && <img className="rmdc-recent-img" src={c.courseImage} alt="" loading="lazy" />}
                  <div className="rmdc-recent-body">
                    <h3>{c.courseName}</h3>
                    <div className="rmdc-recent-meta">
                      <span className="rmdc-recent-avatar" aria-hidden="true">
                        {initialsOf(c.studentName)}
                      </span>
                      {shortName(c.studentName)}
                      {c.date && (
                        <span className="rmdc-recent-date">
                          {new Date(c.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                        </span>
                      )}
                    </div>
                    {c.rating > 0 && (
                      <div className="rmdc-stars" aria-label={`Rated ${c.rating} out of 5`}>
                        {[1, 2, 3, 4, 5].map((n) => (
                          <FontAwesomeIcon key={n} icon={faStar} className={n <= c.rating ? 'is-on' : ''} />
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* ---------- Join dialog ---------- */}
      {dialog && (
        <div className="rmdc-overlay" onClick={() => setDialog(null)}>
          <div
            className="rmdc-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="rmdc-dialog-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="rmdc-dialog-close" onClick={() => setDialog(null)} aria-label="Close">
              <FontAwesomeIcon icon={faXmark} />
            </button>

            <span className={`rmdc-dialog-icon is-${dialog.type}`}>
              <FontAwesomeIcon
                icon={dialog.type === 'early' ? faHourglassHalf : dialog.type === 'expired' ? faCircleCheck : faTriangleExclamation}
              />
            </span>

            <h3 id="rmdc-dialog-title">
              {dialog.type === 'early' && 'Not time yet'}
              {dialog.type === 'expired' && 'This class has ended'}
              {dialog.type === 'error' && 'Couldn’t open the class'}
            </h3>
            {dialog.message && <p>{dialog.message}</p>}
            {dialog.type === 'early' && <p>You can join {JOIN_EARLY_MIN} minutes before the start time.</p>}

            <div className="rmdc-dialog-actions">
              {dialog.type === 'expired' ? (
                <a className="rmdc-btn rmdc-btn--wa" href={waUrl(enrollText)} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faWhatsapp} /> Continue on WhatsApp
                </a>
              ) : dialog.type === 'error' ? (
                <a className="rmdc-btn rmdc-btn--wa" href={waUrl('Hi! I can’t join my free class.')} target="_blank" rel="noopener noreferrer">
                  <FontAwesomeIcon icon={faWhatsapp} /> Message us
                </a>
              ) : null}
              <button type="button" className="rmdc-btn rmdc-btn--ghost" onClick={() => setDialog(null)}>
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoClass;